import React, { useEffect, useId, useState } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import './ConfirmDialog.css';

/**
 * Confirmation for an irreversible action.
 *
 * Replaces the Popovers the admin pages used, which offered a bare "Yes", no
 * Cancel, and no statement of what was about to be destroyed. A MUI Dialog
 * rather than a bespoke overlay, so it inherits the focus trap, the escape
 * handler and the scroll lock — the same reasons AppDetailModal uses one.
 *
 * `confirmWord` gates the confirm button behind retyping the app's name. It is
 * friction on purpose: the two actions on /Update are unrecoverable, and the
 * cost of a wrong click is an entire listing.
 */
export default function ConfirmDialog({
  open,
  title,
  description,
  confirmWord,
  confirmLabel,
  onConfirm,
  onClose,
}) {
  const titleId = useId();
  const inputId = useId();
  const [typed, setTyped] = useState('');

  // Reopening must not inherit the word typed last time, which would leave the
  // dialog armed before it had been read.
  useEffect(() => {
    if (!open) setTyped('');
  }, [open]);

  const armed = !confirmWord || typed.trim() === confirmWord;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby={titleId}
      classes={{ root: 'itcConfirmRoot', paper: 'itcConfirmPaper' }}
    >
      <h2 className="itcConfirmTitle" id={titleId}>
        {title}
      </h2>

      <p className="itcConfirmBody">{description}</p>

      {confirmWord && (
        <div className="itcConfirmType">
          <label className="itcConfirmTypeLabel" htmlFor={inputId}>
            {`Type ${confirmWord} to confirm`}
          </label>
          <input
            id={inputId}
            className="itcConfirmInput"
            type="text"
            value={typed}
            autoComplete="off"
            onChange={(event) => setTyped(event.target.value)}
          />
        </div>
      )}

      <div className="itcConfirmActions">
        <button type="button" className="itcConfirmCancel" onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className="itcConfirmGo"
          disabled={!armed}
          onClick={onConfirm}
        >
          {confirmLabel}
        </button>
      </div>
    </Dialog>
  );
}

ConfirmDialog.propTypes = {
  open: PropTypes.bool,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  confirmWord: PropTypes.string,
  confirmLabel: PropTypes.string.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};
